import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { fetchGrokLeads, takeOverLead, releaseLead } from "../../lib/api";
import { PriorityBadge } from "../ui-bits";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "needs", label: "Needs answer" },
  { id: "new,acked", label: "New / acked" },
  { id: "replied,qualified,rfq", label: "In progress" },
  { id: "won,lost,opt_out,no_response", label: "Closed" },
];

export function LeadsPanel({ reloadKey }) {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState({ total: 0, items: [] });

  const load = useCallback(async () => {
    const params = filter === "needs" ? { needs_answer: true } : filter === "all" ? {} : { status: filter };
    try { setData(await fetchGrokLeads({ ...params, limit: 100 })); } catch { /* shown empty */ }
  }, [filter]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const toggleTakeover = async (l) => {
    try {
      await (l.taken_over ? releaseLead(l.id) : takeOverLead(l.id));
      toast.success(l.taken_over ? "Bot may follow up again" : "You took over — bot paused for this contact");
      await load();
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="border border-zinc-200 bg-white p-5 mb-6" data-testid="grok-leads-panel">
      <div className="overline mb-1">Lead pipeline · needs answer first</div>
      <div className="font-heading font-black text-lg mb-3">Leads <span className="text-zinc-400 font-mono-num text-base">({data.total})</span></div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`grok-leads-filter-${f.id.replace(/,/g, "-")}`} onClick={() => setFilter(f.id)}
            className={`px-2.5 py-1 text-xs font-semibold border transition-colors ${filter === f.id ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:border-zinc-400"}`}>
            {f.label}
          </button>
        ))}
      </div>
      {data.items.length === 0 ? (
        <div className="text-sm text-zinc-400 py-8 text-center" data-testid="grok-leads-empty">No leads here yet.</div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {data.items.map((l) => (
            <div key={l.id} data-testid={`grok-lead-row-${l.id}`} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 sm:w-56 min-w-0">
                <PriorityBadge priority={l.priority} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{l.name || "Unknown"}</div>
                  <div className="text-[11px] text-zinc-400 font-mono-num truncate">{l.phone_e164 || l.contact_key}</div>
                </div>
              </div>
              <div className="flex-1 min-w-0 text-sm text-zinc-600 truncate">{l.first_message}</div>
              <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                {l.needs_answer && <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-[#FF3B30] text-white">Needs answer</span>}
                {l.off_lane && <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-zinc-200 text-zinc-600">Off-lane</span>}
                {l.duplicate_count > 0 && <span className="text-[11px] text-zinc-400">+{l.duplicate_count} dup</span>}
                {l.taken_over && <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-zinc-950 text-white">You</span>}
                <button data-testid={`grok-lead-takeover-${l.id}`} onClick={() => toggleTakeover(l)}
                  className="px-2 py-0.5 text-[11px] font-semibold border border-zinc-300 hover:bg-zinc-50">
                  {l.taken_over ? "Hand back" : "Take over"}
                </button>
                <span data-testid={`grok-lead-status-${l.id}`} className="px-2 py-0.5 text-[11px] font-mono-num border border-zinc-300">{l.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
